import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../services/AuthContext';
import { listItems, createItem, updateItem, deleteItem, getUploadUrl, updateProfile, createStripeConnect, getStripeStatus, getVendorOrders, markOrderShipped, respondToCancel, getVendorBlueprints, createBlueprint, updateBlueprint, publishVersion, getContributions, vendorSubscriptionCheckout, vendorSubscriptionPortal, redeemVendorVoucher, getVendorOffers, respondToOffer } from '../services/api';
import VendorBadge from '../components/VendorBadge';
import Header from '../components/Header';
import { ALL_SKILLS, suggestSkills } from '../data/skills';

function SkillIndexEditor({ vendor, onUpdate, items }) {
  const [search, setSearch] = React.useState('');
  const [suggestions, setSuggestions] = React.useState([]);
  const currentSkills = vendor.skills || [];

  const filtered = search.trim()
    ? ALL_SKILLS.filter(s => s.toLowerCase().includes(search.toLowerCase()) && !currentSkills.includes(s)).slice(0, 8)
    : [];

  function addSkill(skill) {
    if (!currentSkills.includes(skill)) {
      onUpdate([...currentSkills, skill]);
    }
    setSearch('');
  }

  function removeSkill(skill) {
    onUpdate(currentSkills.filter(s => s !== skill));
  }

  function handleSuggest() {
    const allText = [
      vendor.bio || '',
      vendor.specialty || '',
      ...items.map(i => `${i.name} ${i.description || ''} ${(i.woodSpecies || []).join(' ')}`)
    ].join(' ');
    const suggested = suggestSkills(allText).filter(s => !currentSkills.includes(s));
    setSuggestions(suggested);
  }

  return (
    <div className="skill-editor">
      <div className="skill-chips">
        {currentSkills.map(skill => (
          <span className="skill-chip" key={skill}>{skill} <button onClick={() => removeSkill(skill)}>×</button></span>
        ))}
      </div>
      <div className="skill-search-wrap">
        <input
          type="text"
          className="skill-search"
          placeholder="Type to search skills..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {filtered.length > 0 && (
          <div className="skill-dropdown">
            {filtered.map(s => (
              <div key={s} className="skill-dropdown-item" onClick={() => addSkill(s)}>{s}</div>
            ))}
          </div>
        )}
      </div>
      <button className="btn-secondary" onClick={handleSuggest} style={{marginTop:'0.75rem', fontSize:'0.8rem'}}>
        ✨ Suggest skills from my content
      </button>
      {suggestions.length > 0 && (
        <div className="skill-suggestions">
          <p className="settings-hint">Suggested based on your bio and items:</p>
          <div className="skill-chips">
            {suggestions.map(s => (
              <span className="skill-chip suggestion" key={s} onClick={() => addSkill(s)}>+ {s}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ContributionsTab() {
  const [data, setData] = React.useState(null);

  React.useEffect(() => {
    getContributions().then(setData).catch(() => {});
  }, []);

  if (!data) return <p className="empty-state">Loading...</p>;

  return (
    <div className="contributions-section">
      <div className="contrib-stats">
        <div className="contrib-stat"><span className="contrib-num">{data.stats.blueprintsPublished}</span><span className="contrib-label">Plans Published</span></div>
        <div className="contrib-stat"><span className="contrib-num">{data.stats.variationsMade}</span><span className="contrib-label">Variations Made</span></div>
        <div className="contrib-stat"><span className="contrib-num">{data.stats.shopResultsPosted}</span><span className="contrib-label">Builds Posted</span></div>
        <div className="contrib-stat"><span className="contrib-num">{data.stats.totalStars}</span><span className="contrib-label">Stars Received</span></div>
      </div>
      {data.stats.variationsInspired > 0 && (
        <p className="contrib-inspired">🌳 Your plans have inspired {data.stats.variationsInspired} variations</p>
      )}
      {data.badges.length > 0 && (
        <div className="contrib-badges">
          {data.badges.map((b, i) => <span className="contrib-badge" key={i} title={b.name}>{b.icon} {b.name}</span>)}
        </div>
      )}
      {data.originals.length > 0 && (
        <div className="contrib-list">
          <h4>My Blueprints</h4>
          {data.originals.map(bp => (
            <div className="contrib-item" key={bp.blueprintId}>
              <span>{bp.title}</span>
              <span className="contrib-item-stats">★{bp.starCount || 0} • 🔀{bp.variationCount || 0} • 🔨{bp.shopResultCount || 0}</span>
            </div>
          ))}
        </div>
      )}
      {data.variations.length > 0 && (
        <div className="contrib-list">
          <h4>My Variations</h4>
          {data.variations.map(bp => (
            <div className="contrib-item" key={bp.blueprintId}>
              <span>{bp.title}</span>
              <span className="contrib-item-stats">Based on {bp.forkedFrom}</span>
            </div>
          ))}
        </div>
      )}
      {data.shopResults.length > 0 && (
        <div className="contrib-list">
          <h4>My Shop Results</h4>
          {data.shopResults.map(r => (
            <div className="contrib-item" key={r.resultId}>
              <span>{r.notes?.slice(0, 60) || 'Build posted'}</span>
              <span className="contrib-item-stats">{new Date((r.createdAt || 0) * 1000).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      )}
      {data.stats.blueprintsPublished === 0 && data.stats.variationsMade === 0 && data.stats.shopResultsPosted === 0 && (
        <p className="empty-state">No contributions yet. Publish a blueprint, create a variation, or post a shop result to get started!</p>
      )}
    </div>
  );
}

function VendorPlanSection({ vendor }) {
  const [voucherCode, setVoucherCode] = React.useState('');
  const tier = vendor?.vendorTier || 'starter';
  const fee = vendor?.platformFeePercent ?? 8;
  const limit = vendor?.listingLimit ?? 10;

  async function handleUpgrade(targetTier) {
    try {
      const data = await vendorSubscriptionCheckout(targetTier);
      if (data.url) window.location.href = data.url;
    } catch (e) { alert(e.error || 'Failed to start checkout'); }
  }

  async function handlePortal() {
    try {
      const data = await vendorSubscriptionPortal();
      if (data.url) window.location.href = data.url;
    } catch (e) { alert(e.error || 'No active subscription found'); }
  }

  async function handleRedeem() {
    if (!voucherCode.trim()) return;
    try {
      const data = await redeemVendorVoucher(voucherCode.trim());
      alert(data.message || 'Voucher redeemed!');
      window.location.reload();
    } catch (e) { alert(e.error || 'Invalid voucher'); }
  }

  return (
    <div className="vendor-plan-section">
      <h3>Your Plan</h3>
      <div className="vendor-plan-current">
        <span className={`vendor-tier-badge ${tier}`}>{tier.charAt(0).toUpperCase() + tier.slice(1)}</span>
        <div className="vendor-plan-details">
          <p>Transaction fee: <strong>{fee}%</strong> per sale</p>
          <p>Listing limit: <strong>{limit === -1 ? 'Unlimited' : limit}</strong> items</p>
        </div>
      </div>
      {tier !== 'elite' && (
        <div className="vendor-plan-upgrades">
          {tier === 'starter' && (
            <div className="vendor-plan-card" onClick={() => handleUpgrade('pro')}>
              <h4>Pro</h4>
              <p className="vendor-plan-price">$9.99/mo</p>
              <p>4% fee • 50 listings • Spotlight Bid • Commissions</p>
            </div>
          )}
          <div className="vendor-plan-card" onClick={() => handleUpgrade('elite')}>
            <h4>Elite</h4>
            <p className="vendor-plan-price">$24.99/mo</p>
            <p>0% fee • Unlimited listings • All features</p>
          </div>
        </div>
      )}
      {tier !== 'starter' && (
        <button className="btn-secondary" style={{marginTop:'0.75rem'}} onClick={handlePortal}>Manage Subscription</button>
      )}
      <div className="vendor-voucher-row">
        <input type="text" placeholder="Have a voucher code?" value={voucherCode} onChange={e => setVoucherCode(e.target.value)} />
        <button className="btn-secondary" onClick={handleRedeem}>Redeem</button>
      </div>
    </div>
  );
}

function PinnacleBooking({ vendor }) {
  const [weeks, setWeeks] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    const API = process.env.REACT_APP_API_URL || 'https://rj2psn7fng.execute-api.us-east-1.amazonaws.com/v1';
    fetch(`${API}/pinnacle/available`).then(r => r.json()).then(data => setWeeks(data.weeks || [])).catch(() => {});
  }, []);

  async function handleBook(weekStart) {
    setLoading(true);
    try {
      const API = process.env.REACT_APP_API_URL || 'https://rj2psn7fng.execute-api.us-east-1.amazonaws.com/v1';
      const token = localStorage.getItem('ts_token');
      const res = await fetch(`${API}/pinnacle/book`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ weekStart }) });
      const data = await res.json();
      if (!res.ok) throw data;
      if (data.url) window.location.href = data.url;
    } catch (e) { alert(e.error || 'Failed to book'); }
    finally { setLoading(false); }
  }

  return (
    <div className="vendor-plan-section" style={{marginBottom:'1.5rem'}}>
      <h3>🏔️ The Pinnacle — $49/week</h3>
      <p style={{fontSize:'0.85rem', color:'var(--text-muted)', marginBottom:'1rem'}}>Get featured at the top of the Expo Floor for maximum visibility.</p>
      <div style={{display:'flex', flexDirection:'column', gap:'0.5rem'}}>
        {weeks.map(w => (
          <div key={w.weekStart} style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0.5rem 0.75rem', border:'1px solid rgba(200,164,92,0.15)', borderRadius:'var(--radius)'}}>
            <span style={{fontSize:'0.85rem'}}>{w.weekStart} → {w.weekEnd}</span>
            {w.available ? (
              <button className="btn-primary" style={{padding:'0.3rem 0.75rem', fontSize:'0.8rem'}} disabled={loading} onClick={() => handleBook(w.weekStart)}>Book — $49</button>
            ) : (
              <span style={{fontSize:'0.75rem', color:'var(--text-muted)'}}>🔒 {w.bookedBy}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function VendorOffersTab() {
  const [offers, setOffers] = React.useState([]);
  React.useEffect(() => {
    getVendorOffers().then(data => setOffers(data.offers || [])).catch(() => {});
  }, []);

  async function handleRespond(offerId, action) {
    if (action === 'accept' && !window.confirm('Accept this offer? A checkout will be created for the customer.')) return;
    try {
      await respondToOffer(offerId, action);
      setOffers(prev => prev.map(o => o.offerId === offerId ? { ...o, status: action === 'accept' ? 'accepted' : 'declined' } : o));
    } catch (e) { alert(e.error || 'Failed'); }
  }

  return (
    <div>
      {offers.length === 0 && <p className="empty-state">No offers received yet. When customers make offers on your items, they'll appear here.</p>}
      {offers.map(o => (
        <div className="order-card" key={o.offerId}>
          <div className="order-card-info">
            <h4>{o.itemName}</h4>
            <p className="order-price">Offer: ${(o.offerAmount / 100).toFixed(2)} <span style={{color:'var(--text-muted)'}}>(listed: ${(o.listPrice / 100).toFixed(2)})</span></p>
            <p className="order-meta">{new Date((o.createdAt || 0) * 1000).toLocaleDateString()}</p>
            <span className={`order-status ${o.status === 'accepted' ? 'shipped' : o.status === 'declined' ? '' : 'pending'}`}>
              {o.status === 'accepted' ? '✅ Accepted' : o.status === 'declined' ? '❌ Declined' : '⏳ Pending'}
            </span>
            {o.status === 'pending' && (
              <div className="order-tracking-form" style={{marginTop:'0.5rem'}}>
                <button className="btn-primary" onClick={() => handleRespond(o.offerId, 'accept')}>Accept</button>
                <button className="btn-delete" onClick={() => handleRespond(o.offerId, 'decline')}>Decline</button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function BlueprintsTab() {
  const [blueprints, setBps] = React.useState([]);
  const [showForm, setShowForm] = React.useState(false);
  const [editingBp, setEditingBp] = React.useState(null);
  const [bpView, setBpView] = React.useState('active');
  const [form, setForm] = React.useState({ title: '', description: '', difficulty: 'intermediate', estimatedHours: '', estimatedCost: '', price: '', license: 'mit', tags: '', status: 'draft' });
  const [showRevision, setShowRevision] = React.useState(null);
  const [revForm, setRevForm] = React.useState({ version: '', changelog: '', cutList: '', steps: '', tools: '' });

  React.useEffect(() => {
    getVendorBlueprints().then(data => setBps(data.blueprints || [])).catch(() => {});
  }, []);

  const activeBps = blueprints.filter(b => b.status !== 'archived' && b.status !== 'deleted');
  const archivedBps = blueprints.filter(b => b.status === 'archived');

  async function handleCreate(e) {
    e.preventDefault();
    const payload = {
      ...form,
      estimatedHours: parseInt(form.estimatedHours) || 0,
      estimatedCost: parseInt(form.estimatedCost) || 0,
      price: Math.round(parseFloat(form.price || 0) * 100),
      tags: form.tags.split(',').map(t => t.trim()).filter(Boolean)
    };
    try {
      if (editingBp) {
        await updateBlueprint(editingBp, payload);
      } else {
        await createBlueprint(payload);
      }
      setShowForm(false);
      setEditingBp(null);
      setForm({ title: '', description: '', difficulty: 'intermediate', estimatedHours: '', estimatedCost: '', price: '', license: 'mit', tags: '', status: 'draft' });
      getVendorBlueprints().then(data => setBps((data.blueprints || []).filter(b => b.status !== 'deleted')));
    } catch (err) { alert(err.error || 'Failed'); }
  }

  async function handleRevision(e) {
    e.preventDefault();
    try {
      await publishVersion(showRevision, {
        version: revForm.version,
        changelog: revForm.changelog,
        cutList: revForm.cutList ? JSON.parse(revForm.cutList) : [],
        steps: revForm.steps ? JSON.parse(revForm.steps) : [],
        tools: revForm.tools ? revForm.tools.split(',').map(t => t.trim()).filter(Boolean) : []
      });
      setShowRevision(null);
      setRevForm({ version: '', changelog: '', cutList: '', steps: '', tools: '' });
      getVendorBlueprints().then(data => setBps(data.blueprints || []));
    } catch (err) { alert(err.error || 'Failed to publish revision'); }
  }

  return (
    <div>
      <div className="catalog-toolbar" style={{display:'flex', gap:'1rem', alignItems:'center'}}>
        <button className="btn-primary" onClick={() => { setEditingBp(null); setForm({ title: '', description: '', difficulty: 'intermediate', estimatedHours: '', estimatedCost: '', price: '', license: 'mit', tags: '', status: 'draft' }); setShowForm(true); }}>+ New Blueprint</button>
        <button className={`tab ${bpView === 'active' ? 'active' : ''}`} onClick={() => setBpView('active')}>Active ({activeBps.length})</button>
        <button className={`tab ${bpView === 'archived' ? 'active' : ''}`} onClick={() => setBpView('archived')}>Archived ({archivedBps.length})</button>
      </div>

      {showForm && (
        <div className="item-form-overlay">
          <div className="item-form">
            <h3>{editingBp ? 'Edit Blueprint' : 'New Blueprint'}</h3>
            <form onSubmit={handleCreate}>
              <input type="text" placeholder="Blueprint Title *" value={form.title} onChange={e => setForm({...form, title: e.target.value})} required />
              <textarea placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} />
              <select value={form.difficulty} onChange={e => setForm({...form, difficulty: e.target.value})}>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
              <input type="number" placeholder="Est. hours to build" value={form.estimatedHours} onChange={e => setForm({...form, estimatedHours: e.target.value})} />
              <input type="number" placeholder="Est. material cost ($)" value={form.estimatedCost} onChange={e => setForm({...form, estimatedCost: e.target.value})} />
              <input type="number" step="0.01" placeholder="Price ($ or 0 for free)" value={form.price} onChange={e => setForm({...form, price: e.target.value})} />
              <select value={form.license} onChange={e => setForm({...form, license: e.target.value})}>
                <option value="mit">🟢 Open Source (MIT) — anyone can use, modify, sell</option>
                <option value="cc-by">🟡 Creative Commons (CC BY) — must credit</option>
                <option value="protected">🔒 Protected — personal use only</option>
              </select>
              <input type="text" placeholder="Tags (comma-separated)" value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} />
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
              <div className="form-buttons">
                <button type="submit" className="btn-primary">{editingBp ? 'Save Changes' : 'Create Blueprint'}</button>
                <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="catalog-grid">
        {bpView === 'active' && activeBps.length === 0 && <p className="empty-state">No blueprints yet. Create your first building plan!</p>}
        {bpView === 'archived' && archivedBps.length === 0 && <p className="empty-state">No archived blueprints.</p>}
        {(bpView === 'active' ? activeBps : archivedBps).map(bp => (
          <div className="catalog-card" key={bp.blueprintId}>
            <h4>{bp.title}</h4>
            <p className="order-meta">{bp.difficulty} • v{bp.currentVersion}</p>
            <p className="order-price">{bp.price > 0 ? `$${(bp.price/100).toFixed(2)}` : 'Free'}</p>
            <span className={`bp-license-badge ${bp.license}`}>{bp.license === 'mit' ? '🟢 Open' : bp.license === 'cc-by' ? '🟡 CC BY' : '🔒 Protected'}</span>
            <span className={`visibility-badge ${bp.status}`} style={{marginLeft:'0.5rem'}}>{bp.status}</span>
            <div className="catalog-card-actions">
              <button className="btn-secondary" onClick={() => {
                setForm({ title: bp.title, description: bp.description || '', difficulty: bp.difficulty || 'intermediate', estimatedHours: bp.estimatedHours || '', estimatedCost: bp.estimatedCost || '', price: bp.price ? (bp.price/100).toFixed(2) : '', license: bp.license || 'mit', tags: (bp.tags || []).join(', '), status: bp.status || 'draft' });
                setEditingBp(bp.blueprintId);
                setShowForm(true);
              }}>Edit</button>
              {bp.status !== 'archived' && <button className="btn-secondary" onClick={() => { setShowRevision(bp.blueprintId); setRevForm({ version: '', changelog: '', cutList: '', steps: '', tools: '' }); }}>+ Revision</button>}
              <button className="btn-delete" onClick={async () => {
                if (bp.status === 'archived') {
                  try { await updateBlueprint(bp.blueprintId, { status: 'draft' }); getVendorBlueprints().then(data => setBps(data.blueprints || [])); } catch(e) { alert('Failed'); }
                } else {
                  if (!window.confirm('Archive this blueprint? It will be hidden but preserved.')) return;
                  try { await updateBlueprint(bp.blueprintId, { status: 'archived' }); getVendorBlueprints().then(data => setBps(data.blueprints || [])); } catch(e) { alert('Failed'); }
                }
              }}>{bp.status === 'archived' ? 'Unarchive' : 'Archive'}</button>
            </div>
          </div>
        ))}
      </div>

      {showRevision && (
        <div className="item-form-overlay">
          <div className="item-form">
            <h3>Publish New Revision</h3>
            <p className="settings-hint">This adds a new version to your blueprint's mainline. Updates will appear in the Provenance Graph.</p>
            <form onSubmit={handleRevision}>
              <input type="text" placeholder="Version number (e.g. 1.1, 2.0) *" value={revForm.version} onChange={e => setRevForm({...revForm, version: e.target.value})} required />
              <input type="text" placeholder="What changed? (changelog) *" value={revForm.changelog} onChange={e => setRevForm({...revForm, changelog: e.target.value})} required />
              <textarea placeholder="Updated materials list (optional — leave blank to keep previous)" value={revForm.cutList} onChange={e => setRevForm({...revForm, cutList: e.target.value})} rows={3} />
              <textarea placeholder="Updated steps (optional — leave blank to keep previous)" value={revForm.steps} onChange={e => setRevForm({...revForm, steps: e.target.value})} rows={3} />
              <input type="text" placeholder="Tools (comma-separated, optional)" value={revForm.tools} onChange={e => setRevForm({...revForm, tools: e.target.value})} />
              <div className="form-buttons">
                <button type="submit" className="btn-primary">Publish Revision</button>
                <button type="button" className="btn-secondary" onClick={() => setShowRevision(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = React.useState([]);
  const [tracking, setTracking] = React.useState({});

  React.useEffect(() => {
    getVendorOrders().then(data => setOrders(data.orders || [])).catch(() => {});
  }, []);

  async function handleShip(orderId) {
    const t = tracking[orderId];
    if (!t || !t.number) { alert('Enter a tracking number'); return; }
    try {
      await markOrderShipped(orderId, t.number, t.carrier || 'USPS');
      setOrders(prev => prev.map(o => o.orderId === orderId ? { ...o, status: 'shipped', trackingNumber: t.number, trackingCarrier: t.carrier || 'USPS' } : o));
    } catch (err) { alert(err.error || 'Failed to mark shipped'); }
  }

  return (
    <div className="orders-tab">
      {orders.length === 0 && <p className="empty-state">No orders yet. When a customer purchases one of your items, it will appear here for fulfillment.</p>}
      {orders.map(order => (
        <div className="order-card" key={order.orderId}>
          <div className="order-card-image">
            {order.itemImage && <img src={order.itemImage} alt={order.itemName} />}
          </div>
          <div className="order-card-info">
            <h4>{order.itemName}</h4>
            <p className="order-price">${((order.amount || 0) / 100).toFixed(2)}{order.tip > 0 ? ` (+$${(order.tip / 100).toFixed(2)} tip)` : ''}</p>
            <p className="order-meta">Ordered: {new Date((order.createdAt || 0) * 1000).toLocaleDateString()}</p>
            {order.status === 'pending_fulfillment' && (
              <>
                <span className="order-status pending">⏳ Pending Tracking Number</span>
                <div className="order-tracking-form">
                  <input type="text" placeholder="Tracking number" value={tracking[order.orderId]?.number || ''} onChange={e => setTracking(prev => ({ ...prev, [order.orderId]: { ...prev[order.orderId], number: e.target.value } }))} />
                  <select value={tracking[order.orderId]?.carrier || 'USPS'} onChange={e => setTracking(prev => ({ ...prev, [order.orderId]: { ...prev[order.orderId], carrier: e.target.value } }))}>
                    <option value="USPS">USPS</option>
                    <option value="UPS">UPS</option>
                    <option value="FedEx">FedEx</option>
                    <option value="Other">Other</option>
                  </select>
                  <button className="btn-primary" onClick={() => handleShip(order.orderId)}>Submit Tracking ✓</button>
                </div>
              </>
            )}
            {order.status === 'shipped' && (
              <>
                <span className="order-status shipped">✅ Shipped</span>
                <p className="order-meta">Tracking: {order.trackingNumber} ({order.trackingCarrier})</p>
                <p className="order-meta">💰 Payment captured</p>
              </>
            )}
            {order.status === 'cancellation_requested' && (
              <>
                <span className="order-status pending">🔄 Customer requested cancellation</span>
                <p className="order-meta">Respond within 24 hours or it auto-cancels.</p>
                <div className="order-tracking-form">
                  <button className="btn-primary" onClick={async () => {
                    try { await respondToCancel(order.orderId, 'deny'); window.location.reload(); } catch(e) { alert('Failed'); }
                  }}>Deny — I already shipped</button>
                  <button className="btn-delete" onClick={async () => {
                    if (!window.confirm('Approve cancellation? Customer will not be charged.')) return;
                    try { await respondToCancel(order.orderId, 'approve'); window.location.reload(); } catch(e) { alert('Failed'); }
                  }}>Approve Cancellation</button>
                </div>
              </>
            )}
            {order.status === 'cancelled' && (
              <span className="order-status" style={{background:'rgba(213,74,10,0.15)',color:'#f56523'}}>❌ Cancelled</span>
            )}
            {order.status === 'in_production' && (
              <>
                <span className="order-status pending">🛠️ In Production</span>
                {!order.productionStarted && (
                  <button className="btn-secondary" style={{marginTop:'0.5rem'}} onClick={async () => {
                    try {
                      const API_BASE = process.env.REACT_APP_API_URL || 'https://rj2psn7fng.execute-api.us-east-1.amazonaws.com/v1';
                      const token = localStorage.getItem('ts_token');
                      await fetch(`${API_BASE}/vendor/orders/${order.orderId}/start-production`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } });
                      setOrders(prev => prev.map(o => o.orderId === order.orderId ? { ...o, productionStarted: true } : o));
                    } catch (e) { alert('Failed'); }
                  }}>✓ Mark Production Started</button>
                )}
                {order.productionStarted && <p className="order-meta">🔒 Production locked — customer cannot cancel</p>}
                <div className="order-tracking-form" style={{marginTop:'0.75rem'}}>
                  <input type="text" placeholder="Tracking number" value={tracking[order.orderId]?.number || ''} onChange={e => setTracking(prev => ({ ...prev, [order.orderId]: { ...prev[order.orderId], number: e.target.value } }))} />
                  <select value={tracking[order.orderId]?.carrier || 'USPS'} onChange={e => setTracking(prev => ({ ...prev, [order.orderId]: { ...prev[order.orderId], carrier: e.target.value } }))}>
                    <option value="USPS">USPS</option>
                    <option value="UPS">UPS</option>
                    <option value="FedEx">FedEx</option>
                    <option value="Other">Other</option>
                  </select>
                  <button className="btn-primary" onClick={() => handleShip(order.orderId)}>Submit Tracking ✓</button>
                </div>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function ReadinessBanners({ vendor, items, stripeStatus }) {
  const alerts = [];

  if (!vendor.profileImageUrl || vendor.profileImageUrl.length < 5) {
    alerts.push({ id: 'logo', color: 'amber', message: '📷 Upload a business logo to appear on the Expo Floor' });
  }
  if (!vendor.bio || vendor.bio.length < 10) {
    alerts.push({ id: 'bio', color: 'amber', message: '✏️ Add a bio so customers know about your craft' });
  }
  if (!stripeStatus?.connected) {
    alerts.push({ id: 'stripe', color: 'red', message: '💳 Connect Stripe to receive payments from customers' });
  }
  const publishedItems = items.filter(i => i.visibility === 'published');
  if (publishedItems.length === 0) {
    alerts.push({ id: 'publish', color: 'green', message: '📦 Publish at least 1 item to be visible on the Expo Floor' });
  }
  const noPhotos = publishedItems.filter(i => !i.images || i.images.length === 0);
  if (noPhotos.length > 0) {
    alerts.push({ id: 'photos', color: 'green', message: `🖼️ ${noPhotos.length} published item${noPhotos.length > 1 ? 's' : ''} missing photos` });
  }
  if (!vendor.location || !vendor.location.city) {
    alerts.push({ id: 'location', color: 'amber', message: '📍 Add your city and state in Settings so the interactive map fills up showing customers Timber Summit\'s reach' });
  }

  if (alerts.length === 0) return null;

  return (
    <div className="readiness-banners-wrapper">
      <p className="readiness-banners-title">⚠️ Complete these items before you can start selling:</p>
      <div className="readiness-banners">
        {alerts.map(alert => (
          <div className={`readiness-banner banner-${alert.color}`} key={alert.id}>
            <span>{alert.message}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LaunchReadiness({ vendor, items, stripeStatus }) {
  const [results, setResults] = useState(null);

  function runScan() {
    const checks = [];
    const publishedItems = items.filter(i => i.visibility === 'published');

    // Logo
    checks.push({
      label: 'Business logo uploaded',
      pass: !!(vendor.profileImageUrl && vendor.profileImageUrl.length > 5),
      fix: 'Go to Settings → Upload Logo'
    });

    // Bio
    checks.push({
      label: 'Bio filled out',
      pass: !!vendor.bio && vendor.bio.length > 10,
      fix: 'Go to Settings → Edit → add a bio'
    });

    // Stripe
    checks.push({
      label: 'Stripe connected',
      pass: stripeStatus?.connected === true,
      fix: 'Go to Settings → Connect Stripe'
    });

    // At least 1 published item
    checks.push({
      label: 'At least 1 published item',
      pass: publishedItems.length > 0,
      fix: 'Go to Catalog → set an item to Published'
    });

    // All published items have photos
    const itemsWithoutPhotos = publishedItems.filter(i => !i.images || i.images.length === 0);
    checks.push({
      label: 'All published items have photos',
      pass: itemsWithoutPhotos.length === 0,
      fix: itemsWithoutPhotos.length > 0 ? `Missing photos: ${itemsWithoutPhotos.map(i => i.name).join(', ')}` : ''
    });

    // All published items have descriptions
    const itemsWithoutDesc = publishedItems.filter(i => !i.description || i.description.length < 10);
    checks.push({
      label: 'All published items have descriptions',
      pass: itemsWithoutDesc.length === 0,
      fix: itemsWithoutDesc.length > 0 ? `Missing descriptions: ${itemsWithoutDesc.map(i => i.name).join(', ')}` : ''
    });

    // All published items have a price > 0
    const itemsNoPrice = publishedItems.filter(i => !i.price || i.price <= 0);
    checks.push({
      label: 'All published items have a price',
      pass: itemsNoPrice.length === 0,
      fix: itemsNoPrice.length > 0 ? `No price: ${itemsNoPrice.map(i => i.name).join(', ')}` : ''
    });

    // Specialty set
    checks.push({
      label: 'Specialty selected',
      pass: !!vendor.specialty,
      fix: 'Go to Settings → Edit → select a specialty'
    });

    setResults(checks);
  }

  const passCount = results ? results.filter(r => r.pass).length : 0;
  const totalCount = results ? results.length : 0;

  return (
    <div className="readiness-section">
      <h3>Launch Readiness</h3>
      <p className="section-subtitle" style={{textAlign:'left', marginBottom:'1.5rem'}}>
        Scan your booth to check if everything is ready for customers.
      </p>
      <button className="btn-primary" onClick={runScan}>🔍 Run Scan</button>

      {results && (
        <div className="readiness-results">
          <div className="readiness-score">
            <span className={passCount === totalCount ? 'score-perfect' : 'score-partial'}>
              {passCount}/{totalCount} checks passed
            </span>
            {passCount === totalCount && <span className="readiness-ready">🚀 Ready to launch!</span>}
          </div>
          <div className="readiness-checks">
            {results.map((check, i) => (
              <div className={`readiness-check ${check.pass ? 'pass' : 'fail'}`} key={i}>
                <span className="check-icon">{check.pass ? '✅' : '❌'}</span>
                <span className="check-label">{check.label}</span>
                {!check.pass && <span className="check-fix">{check.fix}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function EditableSettings({ vendor, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    businessName: vendor.businessName || '',
    specialty: vendor.specialty || '',
    bio: vendor.bio || '',
    featuredVideoUrl: vendor.featuredVideoUrl || '',
    youtubeChannelUrl: vendor.youtubeChannelUrl || '',
    city: vendor.location?.city || '',
    state: vendor.location?.state || ''
  });
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      const payload = { ...form };
      // Geocode city/state via Nominatim
      if (payload.city) {
        try {
          const q = encodeURIComponent(`${payload.city}, ${payload.state || ''}`);
          const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1`);
          const geo = await res.json();
          payload.location = { city: payload.city, state: payload.state, country: 'US' };
          if (geo.length > 0) {
            payload.location.lat = geo[0].lat;
            payload.location.lng = geo[0].lon;
          }
        } catch (e) {
          payload.location = { city: payload.city, state: payload.state, country: 'US' };
        }
      }
      delete payload.city;
      delete payload.state;
      // Extract YouTube channel ID from URL if provided
      if (payload.youtubeChannelUrl) {
        const match = payload.youtubeChannelUrl.match(/channel\/(UC[a-zA-Z0-9_-]+)/);
        if (match) {
          payload.youtubeChannelId = match[1];
        }
      }
      await updateProfile(payload);
      onUpdate({ ...vendor, ...payload });
      setEditing(false);
    } catch (err) {
      alert('Failed to update');
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <div className="settings-info">
        <p><strong>Name:</strong> {vendor.businessName}</p>
        <p><strong>Email:</strong> {vendor.email}</p>
        <p><strong>Specialty:</strong> {vendor.specialty || 'Not set'}</p>
        <p><strong>Bio:</strong> {vendor.bio || 'Not set'}</p>
        <button className="btn-secondary" onClick={() => setEditing(true)} style={{marginTop: '0.75rem'}}>Edit</button>
      </div>
    );
  }

  return (
    <div className="settings-edit-form">
      <input type="text" placeholder="Business Name" value={form.businessName} onChange={e => setForm({ ...form, businessName: e.target.value })} />
      <select value={form.specialty} onChange={e => setForm({ ...form, specialty: e.target.value })}>
        <option value="">Select Specialty</option>
        <option value="Fine Furniture">Fine Furniture</option>
        <option value="Rustic Decor">Rustic Decor</option>
        <option value="Small Goods">Small Goods</option>
        <option value="Reclaimed Art">Reclaimed Art</option>
        <option value="Joinery">Joinery</option>
        <option value="Woodturning">Woodturning</option>
        <option value="Carving">Carving</option>
        <option value="Other">Other</option>
      </select>
      <textarea placeholder="Bio" value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} rows={3} />
      <input type="text" placeholder="Featured YouTube Video URL" value={form.featuredVideoUrl} onChange={e => setForm({ ...form, featuredVideoUrl: e.target.value })} />
      <input type="text" placeholder="YouTube Channel URL (e.g., youtube.com/@YourChannel)" value={form.youtubeChannelUrl} onChange={e => setForm({ ...form, youtubeChannelUrl: e.target.value })} />
      <div className="location-fields">
        <input type="text" placeholder="City" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} />
        <input type="text" placeholder="State (e.g., OR)" value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} maxLength={2} />
      </div>
      <div className="form-buttons">
        <button className="btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        <button className="btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
      </div>
    </div>
  );
}

function VendorDashboard() {
  const { vendor, loading, logout, loginVendor } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('catalog');
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(getEmptyForm());
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [itemImages, setItemImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);

  function getEmptyForm() {
    return { name: '', description: '', woodSpecies: '', price: '', pricingType: 'fixed', stockQuantity: 1, visibility: 'draft', videoUrl: '', itemType: 'regular', processingDays: 14 };
  }

  useEffect(() => {
    if (loading) return;
    if (!vendor) { navigate('/vendor/login'); return; }
    loadItems();
    loadStripeStatus();
  }, [vendor, loading]);

  async function loadStripeStatus() {
    try {
      const data = await getStripeStatus();
      setStripeStatus(data);
    } catch (err) {
      setStripeStatus({ connected: false, status: 'error' });
    }
  }

  async function handleStripeConnect() {
    try {
      const data = await createStripeConnect();
      window.location.href = data.url;
    } catch (err) {
      setError('Failed to start Stripe onboarding');
    }
  }

  async function loadItems() {
    try {
      const data = await listItems();
      setItems(data.items || []);
    } catch (err) {
      setError('Failed to load catalog');
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    const payload = {
      ...form,
      price: Math.round(parseFloat(form.price) * 100) || 0,
      woodSpecies: form.woodSpecies.split(',').map(s => s.trim()).filter(Boolean),
      stockQuantity: parseInt(form.stockQuantity) || 1,
      processingDays: parseInt(form.processingDays) || 14,
      images: itemImages
    };

    try {
      if (editingItem) {
        await updateItem(editingItem.itemId, payload);
      } else {
        await createItem(payload);
      }
      setShowForm(false);
      setEditingItem(null);
      setForm(getEmptyForm());
      loadItems();
    } catch (err) {
      setError(err.error || 'Failed to save item');
    }
  }

  async function handleDelete(itemId) {
    if (!window.confirm('Delete this item?')) return;
    try {
      await deleteItem(itemId);
      loadItems();
    } catch (err) {
      setError('Failed to delete item');
    }
  }

  function handleEdit(item) {
    setEditingItem(item);
    setForm({
      name: item.name,
      description: item.description || '',
      woodSpecies: (item.woodSpecies || []).join(', '),
      price: (item.price / 100).toFixed(2),
      pricingType: item.pricingType || 'fixed',
      stockQuantity: item.stockQuantity || 1,
      visibility: item.visibility || 'draft',
      videoUrl: item.videoUrl || '',
      itemType: item.itemType || 'regular',
      processingDays: item.processingDays || 14
    });
    setItemImages(item.images || []);
    setShowForm(true);
  }

  async function handleItemImageUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) { setError('Image must be JPEG, PNG, or WebP'); return; }
    setUploadingImage(true);
    try {
      const itemId = editingItem ? editingItem.itemId : 'temp';
      const { uploadUrl, imageUrl, imageId } = await getUploadUrl(itemId, file.name, file.type);
      await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
      setItemImages(prev => [...prev, { imageId, url: imageUrl, order: prev.length }]);
    } catch (err) {
      setError('Failed to upload image');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  }

  async function handleLogoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Logo must be a JPEG, PNG, or WebP image');
      return;
    }

    setUploading(true);
    setError('');
    try {
      const { uploadUrl, imageUrl } = await getUploadUrl('logo', file.name, file.type);
      await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file
      });
      await updateProfile({ profileImageUrl: imageUrl });
      loginVendor(localStorage.getItem('ts_token'), { ...vendor, profileImageUrl: imageUrl });
    } catch (err) {
      setError('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  }

  if (loading || !vendor) return null;

  return (
    <div className="dashboard-page">
      <Header />
      <div className="dashboard-header">
        <div style={{display:'flex', alignItems:'center', gap:'1rem'}}>
          <div>
            <h2>🏔️ Vendor Dashboard</h2>
            <p className="dashboard-welcome">Welcome, {vendor.businessName}</p>
            {vendor.vendorStatus === 'pending' && (
              <span className="vendor-status-badge pending">⏳ Pending Approval — Set up your booth while you wait</span>
            )}
            {vendor.vendorStatus === 'active' && (
              <span className="vendor-status-badge active">✓ Approved</span>
          )}
          </div>
          <VendorBadge tier={vendor.vendorTier} />
        </div>
        <div className="dashboard-actions">
          {vendor.email === process.env.REACT_APP_ADMIN_EMAIL && (
            <button className="btn-primary" onClick={() => navigate('/admin')}>⚙️ Admin</button>
          )}
        </div>
      </div>

      <ReadinessBanners vendor={vendor} items={items} stripeStatus={stripeStatus} />

      <div className="dashboard-tabs">
        <button className={`tab ${activeTab === 'catalog' ? 'active' : ''}`} onClick={() => setActiveTab('catalog')}>
          Catalog
        </button>
        <button className={`tab ${activeTab === 'orders' ? 'active' : ''}`} onClick={() => setActiveTab('orders')}>
          Orders
        </button>
        <button className={`tab ${activeTab === 'offers' ? 'active' : ''}`} onClick={() => setActiveTab('offers')}>
          Offers
        </button>
        <button className={`tab ${activeTab === 'blueprints' ? 'active' : ''}`} onClick={() => setActiveTab('blueprints')}>
          Blueprints
        </button>
        <button className={`tab ${activeTab === 'contributions' ? 'active' : ''}`} onClick={() => setActiveTab('contributions')}>
          My Contributions
        </button>
        <button className={`tab ${activeTab === 'readiness' ? 'active' : ''}`} onClick={() => setActiveTab('readiness')}>
          Launch Readiness
        </button>
        <button className={`tab ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>
          Settings
        </button>
      </div>

      {error && <div className="auth-error">{error}</div>}

      {activeTab === 'catalog' && (
        <>
          <div className="catalog-toolbar">
            <button className="btn-primary" onClick={() => { setShowForm(true); setEditingItem(null); setForm(getEmptyForm()); setItemImages([]); }}>
              + Add Item
            </button>
          </div>

          {showForm && (
            <div className="item-form-overlay">
              <div className="item-form">
                <h3>{editingItem ? 'Edit Item' : 'Add New Item'}</h3>
                <form onSubmit={handleSubmit}>
                  <input type="text" placeholder="Item Name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
                  <textarea placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} />
                  <input type="text" placeholder="Wood Species (comma-separated)" value={form.woodSpecies} onChange={e => setForm({ ...form, woodSpecies: e.target.value })} />
                  <input type="number" placeholder="Price (USD)" step="0.01" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} />
                  <select value={form.pricingType} onChange={e => setForm({ ...form, pricingType: e.target.value })}>
                    <option value="fixed">Fixed Price</option>
                    <option value="bid">Bid Eligible</option>
                    <option value="both">Both</option>
                  </select>
                  <select value={form.itemType} onChange={e => setForm({ ...form, itemType: e.target.value })}>
                    <option value="regular">Regular (ships within 7 days)</option>
                    <option value="made_to_order">🛠️ Made to Order (built after purchase)</option>
                  </select>
                  {form.itemType === 'made_to_order' ? (
                    <input type="number" placeholder="Processing time (days)" value={form.processingDays} onChange={e => setForm({ ...form, processingDays: e.target.value })} min={7} max={30} />
                  ) : (
                    <input type="number" placeholder="Stock Quantity" value={form.stockQuantity} onChange={e => setForm({ ...form, stockQuantity: e.target.value })} min={0} />
                  )}
                  <select value={form.visibility} onChange={e => setForm({ ...form, visibility: e.target.value })}>
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                  <div className="item-images-section">
                    <label className="image-upload-label">YouTube Video (optional)</label>
                    <input type="text" placeholder="https://www.youtube.com/watch?v=..." value={form.videoUrl} onChange={e => setForm({ ...form, videoUrl: e.target.value })} />
                  </div>
                  <div className="item-images-section">
                    <label className="image-upload-label">Photos (up to 5)</label>
                    <div className="item-images-preview">
                      {itemImages.map((img, idx) => (
                        <div className="item-thumb" key={idx}>
                          <img src={img.url} alt="" />
                          <button type="button" className="thumb-remove" onClick={() => setItemImages(prev => prev.filter((_, i) => i !== idx))}>×</button>
                        </div>
                      ))}
                      {itemImages.length < 5 && (
                        <label className="item-thumb add-thumb">
                          {uploadingImage ? '...' : '+'}
                          <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleItemImageUpload} disabled={uploadingImage} hidden />
                        </label>
                      )}
                    </div>
                  </div>
                  <div className="form-buttons">
                    <button type="submit" className="btn-primary">{editingItem ? 'Save Changes' : 'Create Item'}</button>
                    <button type="button" className="btn-secondary" onClick={() => { setShowForm(false); setEditingItem(null); }}>Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="catalog-grid">
            {items.length === 0 && <p className="empty-state">No items yet. Add your first catalog item!</p>}
            {items.map(item => (
              <div className="catalog-card" key={item.itemId}>
                <div className="catalog-card-image">
                  {item.images && item.images.length > 0 && <img src={item.images[0].url} alt={item.name} className="catalog-card-img" />}
                </div>
                <h4>{item.name}</h4>
                <p className="catalog-price">${(item.price / 100).toFixed(2)}</p>
                <span className={`visibility-badge ${item.visibility}`}>{item.visibility}</span>
                <div className="catalog-card-actions">
                  <button className="btn-secondary" onClick={() => handleEdit(item)}>Edit</button>
                  <button className="btn-delete" onClick={() => handleDelete(item.itemId)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {activeTab === 'orders' && (
        <OrdersTab />
      )}

      {activeTab === 'offers' && (
        <VendorOffersTab />
      )}

      {activeTab === 'blueprints' && (
        <BlueprintsTab />
      )}

      {activeTab === 'contributions' && (
        <ContributionsTab />
      )}

      {activeTab === 'readiness' && (
        <LaunchReadiness vendor={vendor} items={items} stripeStatus={stripeStatus} />
      )}

      {activeTab === 'settings' && (
        <div className="settings-section">
          <VendorPlanSection vendor={vendor} />
          <PinnacleBooking vendor={vendor} />
          <h3>Business Settings</h3>
          <div className="settings-grid-free">
          <div className="settings-card settings-card-wide">
            <div className="settings-profile-row">
              <div className="logo-section">
                <div className="logo-preview">
                  {vendor.profileImageUrl ? (
                    <img src={vendor.profileImageUrl} alt="Business logo" className="logo-image" />
                  ) : (
                    <div className="logo-placeholder">No logo</div>
                  )}
                </div>
                <label className="btn-secondary upload-btn">
                  {uploading ? '...' : 'Change Logo'}
                  <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleLogoUpload} disabled={uploading} hidden />
                </label>
              </div>
              <div className="settings-profile-info">
                <EditableSettings vendor={vendor} onUpdate={(updated) => loginVendor(localStorage.getItem('ts_token'), updated)} />
              </div>
            </div>
          </div>

          <div className="settings-card">
            <h4>💳 Stripe Payments</h4>
            <p className="settings-hint">Connect your Stripe account to receive payments directly from customers.</p>
            {stripeStatus ? (
              <div className="stripe-status">
                {stripeStatus.connected ? (
                  <div className="stripe-connected">
                    <span className="stripe-badge active">✓ Connected</span>
                    <p>Your account is active and can receive payments.</p>
                  </div>
                ) : stripeStatus.status === 'pending' ? (
                  <div className="stripe-pending">
                    <span className="stripe-badge pending">⏳ Pending</span>
                    <p>Stripe is reviewing your account. This usually takes 1-2 days.</p>
                  </div>
                ) : stripeStatus.status === 'incomplete' ? (
                  <div className="stripe-incomplete">
                    <span className="stripe-badge incomplete">⚠️ Incomplete</span>
                    <p>You started onboarding but didn't finish.</p>
                    <button className="btn-primary" onClick={handleStripeConnect}>Complete Setup</button>
                  </div>
                ) : (
                  <button className="btn-primary" onClick={handleStripeConnect}>Connect Stripe</button>
                )}
              </div>
            ) : (
              <p className="settings-hint">Loading...</p>
            )}
          </div>

          <div className="settings-card settings-card-narrow">
            <h4>⚡ Spotlight Bid</h4>
            <p className="settings-hint">Opt in to have one of your items randomly featured in the weekly Spotlight Bid auction.</p>
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={vendor.spotlightOptIn || false}
                onChange={async (e) => {
                  const val = e.target.checked;
                  await updateProfile({ spotlightOptIn: val });
                  loginVendor(localStorage.getItem('ts_token'), { ...vendor, spotlightOptIn: val });
                }}
              />
              <span className="toggle-text">{vendor.spotlightOptIn ? 'Opted In' : 'Opted Out'}</span>
            </label>
          </div>

          <div className="settings-card">
            <h4>🛠️ Skill Index</h4>
            <p className="settings-hint">Add your skills so customers can find you by expertise.</p>
            <SkillIndexEditor vendor={vendor} onUpdate={async (updated) => {
              await updateProfile({ skills: updated });
              loginVendor(localStorage.getItem('ts_token'), { ...vendor, skills: updated });
            }} items={items} />
          </div>

          <div className="settings-card">
            <h4>💰 Offer Threshold</h4>
            <p className="settings-hint">Set the minimum offer customers can make as a percentage of the listed price.</p>
            <div className="threshold-control">
              <input
                type="range"
                min="50"
                max="100"
                value={vendor.offerThreshold || 80}
                onChange={async (e) => {
                  const val = parseInt(e.target.value);
                  await updateProfile({ offerThreshold: val });
                  loginVendor(localStorage.getItem('ts_token'), { ...vendor, offerThreshold: val });
                }}
                className="threshold-slider"
              />
              <span className="threshold-value">{vendor.offerThreshold || 80}% of listed price</span>
              <p className="threshold-example">Example: A $100 item can receive offers of ${((vendor.offerThreshold || 80) / 100 * 100).toFixed(0)} or higher</p>
            </div>
          </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VendorDashboard;
